import {Component, OnInit, HostListener, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {
    PerFormaAPIService,
    DocumentDto,
    DocumentKeywordDto,
    SearchDocumentTypeDto,
    SearchNoteDto
} from "src/app/services/pfapi.service";
import {FileDownloadService} from "src/app/services/file-download.service";

@Component({
    selector: "app-document-edit",
    templateUrl: "./document-edit.component.html",
    styleUrl: "./document-edit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentEditComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private route: ActivatedRoute,
        private router: Router,
        private message: NzMessageService,
        private fileDownload: FileDownloadService
    ) {}

    loading = true;

    // id of the file being fetched right now, so the clicked link can show a spinner
    downloadingFileId: number | null = null;

    // opens a document file through the authed client (a plain <a href> would 401 against the bearer-only API)
    openDocFile = async (id: number | undefined | null) => {
        if (id == null) return;
        // ignore repeat clicks on the same link while its fetch is still in flight
        if (this.downloadingFileId === id) return;
        this.downloadingFileId = id;
        try {
            await this.fileDownload.openDocumentFile(id, true);
        } finally {
            this.downloadingFileId = null;
        }
    };

    // the following object is used to check
    // if any unsaved changes are present when
    // the user attempts to navigate away
    originalDocument: DocumentDto | null = null;

    document: DocumentDto = {
        Name: "",
        DocumentType: {
            Name: ""
        } as SearchDocumentTypeDto,
        Keywords: [],
        Files: [{FileType: ""}]
    };

    hasHistory = false;
    selectedKeyword = "";
    selectableKeywords: {label: string; value: string}[] = [];
    docKeywords: DocumentKeywordDto[] = [];
    currentNote = "";

    async ngOnInit() {
        const id = this.route.snapshot.queryParamMap.get("id");

        if (!id) {
            this.router.navigateByUrl("/search?previous=true");
        }

        // check if this document is being opened in a new window
        this.hasHistory = window.history.length > 1;

        try {
            this.document = await lastValueFrom(this.api.getApiDocumentId(Number(id)));
            this.originalDocument = JSON.parse(JSON.stringify(this.document));
            this.doSetup();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    }

    doSetup = () => {
        this.setKeywords();
        this.setNotes();
    };

    save = async () => {
        this.loading = true;

        try {
            const resp = await lastValueFrom(this.api.putApiDocument(this.document));
            this.document = resp;
            this.message.success("Saved changes");
            this.updateOriginalData();
            this.doSetup();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    //#region Keywords

    setSelectableKeywords = () => {
        if (this.document.CanEditKeywordData) {
            this.selectableKeywords = this.document.Keywords.filter(x => x.ID == null).map(x => {
                return {
                    label: x.Name,
                    value: x.MetaDataDefinitionID.toString()
                };
            });
        } else {
            this.selectableKeywords = [];
        }
    };

    setDocumentKeywords = () => {
        this.docKeywords = this.document.Keywords.filter(x => x.ID != null);
    };

    setKeywords = () => {
        this.selectedKeyword = "";
        this.setSelectableKeywords();
        this.setDocumentKeywords();
    };

    addKeyword = (id: string) => {
        const item = this.document.Keywords.find(x => x.MetaDataDefinitionID === Number(id) && x.ID == null);

        if (item) {
            item.ID = 0;
            this.setKeywords();
        }
    };

    // if they are removing a user row, we
    // just null out the unique id
    // so that it will go back into the dropdown of available users
    removeKeyword = (id: number) => {
        const item = this.document.Keywords.find(x => x.MetaDataDefinitionID === id && x.ID != null);

        if (item) {
            item.ID = null;
            this.setKeywords();
        }
    };

    AnyKeywordIsRequired = () => {
        return this.document.Keywords.some(x => x.Required);
    };

    //#endregion

    //#region Notes

    setNotes = () => {
        this.currentNote = "";
    };

    addNote = () => {
        const s = this.currentNote.trim();

        if (!s) return;

        const newNote: SearchNoteDto = {
            ID: this.document.Notes.length,
            Content: s,
            // here we convert the current time to UTC then chop off the 'Z' at the end
            // so the DatePipe will correctly convert it
            Timestamp: new Date().toISOString().slice(0, -1),
            User: "you"
        };

        this.document.Notes = [newNote, ...this.document.Notes];

        this.setNotes();
    };

    // if they are removing a note, we
    // remove it from the actual collection
    // so that it will be deleted on the server
    removeNote = (id: number) => {
        const item = this.document.Notes.find(x => x.ID === id);
        if (item) this.document.Notes = this.document.Notes.filter(x => x.ID != id);
    };

    //#endregion

    //#region Helper Methods

    // check for unsaved changes
    isUnsavedChanges(): boolean {
        if (!this.originalDocument) {
            return false;
        }

        return JSON.stringify(this.document) !== JSON.stringify(this.originalDocument);
    }

    // listen for browser refresh/close
    @HostListener("window:beforeunload", ["$event"])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.isUnsavedChanges()) {
            event.preventDefault();

            // Legacy/fallback: Set if the property exists (future-proofing)
            if ("returnValue" in event) {
                event.returnValue = true;
            }
        }
    }

    // when various sections are saved, update the original data to mark it "clean"
    updateOriginalData = () => {
        this.originalDocument = JSON.parse(JSON.stringify(this.document));
    };

    goBack = () => {
        if (this.isUnsavedChanges()) {
            if (!confirm("You have unsaved changes. Are you sure you want to leave?")) {
                return;
            }
        }

        this.router.navigateByUrl("/search?previous=true");
    };

    reset = () => {
        this.document = JSON.parse(JSON.stringify(this.originalDocument));
        this.doSetup();
    };

    //#endregion
}
